// ── BCH Live — Creator Panel ──────────────────────────────────────
//
// The creator identity block shown beside/below the player, and the
// basis of the creator page treatment:
//
//   banner · avatar · bio · live status · followers · following ·
//   categories · social links · creator statistics · scheduled
//   streams · previous broadcasts
//
// Every number here is derived from events we already query elsewhere,
// so the panel adds one cheap kind 30311 query (the creator's own
// broadcast history) and nothing else.

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  User, Radio, Users, Globe, Coins, Calendar, History,
  ChevronDown, ChevronUp, Trophy, Video, Clock,
} from 'lucide-react';
import { useNostrQuery, useNostrProfile } from '../../hooks/useNostrQuery';
import { useFollowers } from '../../hooks/useFollows';
import { STREAM_EVENT_KIND, parseStreamEvent } from '../../lib/streamCredentials';
import {
  toLiveStream, formatScheduledTime, formatViewers, type LiveStream,
} from '../../lib/liveStream';
import { formatUsd } from './StreamOverlays';
import { CountdownBadge, streamHref } from './StreamCard';
import FollowButton from '../FollowButton';
import VerifiedBadge from '../VerifiedBadge';
import { useNip05Verified } from '../../hooks/useNip05Verified';

// ══════════════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════════════

/**
 * https/http allowlist for profile-supplied links.
 *
 * Profile metadata is attacker-controlled, so a raw `website` value can
 * carry `javascript:` and turn a click into script execution. Anything
 * that isn't an http(s) URL is dropped entirely.
 */
function safeHref(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const u = new URL(candidate);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    return u.href;
  } catch {
    return null;
  }
}

function hostOf(href: string): string {
  try {
    return new URL(href).hostname.replace(/^www\./, '');
  } catch {
    return href;
  }
}

/** Compact stat count, e.g. 1234 → "1.2K". */
function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return String(n);
}

// ══════════════════════════════════════════════════════════════════
//  FOLLOWING COUNT
// ══════════════════════════════════════════════════════════════════

/**
 * Size of an arbitrary user's kind 3 contact list.
 *
 * Deliberately NOT `useFollows`: that hook owns a module-global singleton
 * for the *logged-in* user's follow list, and it treats any pubkey as the
 * current user while that singleton is still uninitialised. Calling it with
 * a third party's pubkey can therefore capture the global slot — which also
 * guards kind 3 publishing, where a wrong list wipes someone's contacts.
 *
 * A plain read-only query has none of that coupling.
 */
function useFollowingCount(pubkey: string | null | undefined): number {
  const filters = useMemo(
    () => (pubkey ? [{ kinds: [3], authors: [pubkey], limit: 1 }] : []),
    [pubkey],
  );
  const { events } = useNostrQuery({ filters, enabled: !!pubkey, allRelays: true });

  return useMemo(() => {
    if (events.length === 0) return 0;
    // Relays disagree about which version of a replaceable event they hold.
    // Prefer the newest, breaking ties toward the more complete list.
    let best = events[0];
    let bestCount = best.tags.filter(t => t[0] === 'p').length;
    for (const ev of events.slice(1)) {
      const count = ev.tags.filter(t => t[0] === 'p').length;
      if (ev.created_at > best.created_at
        || (ev.created_at === best.created_at && count > bestCount)) {
        best = ev;
        bestCount = count;
      }
    }
    return new Set(
      best.tags.filter(t => t[0] === 'p' && t[1]?.length === 64).map(t => t[1]),
    ).size;
  }, [events]);
}

// ══════════════════════════════════════════════════════════════════
//  CREATOR BROADCAST HISTORY
// ══════════════════════════════════════════════════════════════════

export interface CreatorBroadcasts {
  live: LiveStream[];
  scheduled: LiveStream[];
  past: LiveStream[];
  totalBroadcasts: number;
  loading: boolean;
}

/**
 * Every kind 30311 this creator has published in the last 30 days,
 * collapsed per `d` coordinate and bucketed.
 *
 * Author-filtered, so nobody can inject broadcasts into someone else's
 * history by reusing their `d` tag.
 */
export function useCreatorBroadcasts(pubkey: string | null | undefined): CreatorBroadcasts {
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 30);

  const filters = useMemo(
    () => (pubkey ? [{ kinds: [STREAM_EVENT_KIND], authors: [pubkey], limit: 60, since }] : []),
    [pubkey, since],
  );

  const { events, loading } = useNostrQuery({ filters, enabled: !!pubkey, allRelays: true });

  return useMemo(() => {
    const byD = new Map<string, typeof events[number]>();
    for (const ev of events) {
      const d = ev.tags.find(t => t[0] === 'd')?.[1] || '';
      const prev = byD.get(d);
      if (!prev || ev.created_at > prev.created_at) byD.set(d, ev);
    }

    const all: LiveStream[] = [];
    for (const ev of byD.values()) {
      all.push(toLiveStream(parseStreamEvent(ev), ev.tags));
    }

    const live = all.filter(s => s.isLive);
    const scheduled = all
      .filter(s => s.isUpcoming)
      .sort((a, b) => (a.startsAt || Infinity) - (b.startsAt || Infinity));
    const past = all
      .filter(s => s.hasEnded)
      .sort((a, b) => Math.max(b.endsAt, b.created_at) - Math.max(a.endsAt, a.created_at));

    return { live, scheduled, past, totalBroadcasts: all.length, loading };
  }, [events, loading]);
}

// ══════════════════════════════════════════════════════════════════
//  STAT PILL
// ══════════════════════════════════════════════════════════════════

function Stat({
  icon: Icon, value, label, accent,
}: {
  icon: React.ComponentType<{ className?: string }>;
  value: string;
  label: string;
  accent?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col items-center rounded-xl bg-muted/50 px-2 py-2 text-center">
      <Icon className={`mb-0.5 h-3.5 w-3.5 ${accent || 'text-muted-foreground'}`} />
      <span className="truncate text-sm font-extrabold tabular-nums">{value}</span>
      <span className="truncate text-[10px] font-medium text-muted-foreground">{label}</span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  BROADCAST ROW
// ══════════════════════════════════════════════════════════════════

function BroadcastRow({ stream, nip05 }: { stream: LiveStream; nip05?: string }) {
  const nip05Verified = useNip05Verified(nip05, stream.hostPubkey);
  return (
    <Link
      to={streamHref(stream, nip05, nip05Verified)}
      className="group flex items-center gap-2.5 rounded-xl p-1.5 transition-colors hover:bg-muted/60"
    >
      <div className="relative h-11 w-20 shrink-0 overflow-hidden rounded-lg bg-muted">
        {stream.image ? (
          <img src={stream.image} alt="" loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div
            className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${
              stream.category?.gradient || 'from-emerald-500/25 to-teal-700/25'
            }`}
          >
            <Video className="h-4 w-4 text-white/50" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-bold transition-colors group-hover:text-primary">
          {stream.title}
        </p>
        <p className="truncate text-[10px] text-muted-foreground">
          {stream.isUpcoming
            ? formatScheduledTime(stream.startsAt)
            : stream.category?.label || 'Broadcast'}
        </p>
      </div>
      {stream.isUpcoming && <CountdownBadge startsAt={stream.startsAt} />}
      {stream.isLive && stream.viewerCount > 0 && (
        <span className="shrink-0 text-[10px] font-bold text-red-500">
          {formatViewers(stream.viewerCount)}
        </span>
      )}
    </Link>
  );
}

// ══════════════════════════════════════════════════════════════════
//  COLLAPSIBLE SECTION
// ══════════════════════════════════════════════════════════════════

function Collapsible({
  icon: Icon, title, count, children, defaultOpen = false,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  count: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (count === 0) return null;

  return (
    <div className="border-t border-border pt-2.5">
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-lg px-1 py-1 text-left transition-colors hover:bg-muted/50"
      >
        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="text-xs font-bold">{title}</span>
        <span className="rounded bg-muted px-1.5 text-[10px] font-bold text-muted-foreground">
          {count}
        </span>
        {open
          ? <ChevronUp className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
          : <ChevronDown className="ml-auto h-3.5 w-3.5 text-muted-foreground" />}
      </button>
      {open && <div className="mt-1 space-y-0.5">{children}</div>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  PANEL
// ══════════════════════════════════════════════════════════════════

export interface CreatorPanelProps {
  pubkey: string;
  isLive?: boolean;
  /** Total USD tipped to this creator in the current window */
  totalTippedUsd?: number;
  /** Distinct supporters in the current window */
  supporterCount?: number;
  /** Category of the current/most recent stream */
  categoryLabel?: string | null;
  className?: string;
}

export default function CreatorPanel({
  pubkey, isLive = false, totalTippedUsd = 0, supporterCount = 0,
  categoryLabel, className = '',
}: CreatorPanelProps) {
  const { profile } = useNostrProfile(pubkey);
  const { followerCount } = useFollowers(pubkey);
  const followingCount = useFollowingCount(pubkey);
  const { scheduled, past, totalBroadcasts } = useCreatorBroadcasts(pubkey);

  const name = profile?.display_name || profile?.name || pubkey.slice(0, 10);
  const website = safeHref(profile?.website);
  const banner = safeHref(profile?.banner);
  const picture = safeHref(profile?.picture);
  const [bioOpen, setBioOpen] = useState(false);

  const about: string = typeof profile?.about === 'string' ? profile.about : '';
  const longBio = about.length > 150;

  return (
    <div className={`overflow-hidden rounded-2xl border border-border bg-card ${className}`}>
      {/* ── Banner ── */}
      <div className="relative h-20 bg-gradient-to-r from-emerald-500/25 via-primary/15 to-amber-500/20">
        {banner && (
          <img src={banner} alt="" loading="lazy" className="h-full w-full object-cover" />
        )}
        {isLive && (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-md bg-red-600 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white shadow-sm">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
            Live
          </span>
        )}
      </div>

      <div className="px-4 pb-4">
        {/* ── Avatar + follow ── */}
        <div className="-mt-7 flex items-end justify-between gap-2">
          <Link to={`/profile/${pubkey}`} className="shrink-0">
            {picture ? (
              <img
                src={picture}
                alt=""
                className="h-14 w-14 rounded-2xl border-2 border-card object-cover shadow-md"
              />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl border-2 border-card bg-primary/10 shadow-md">
                <User className="h-6 w-6 text-primary/60" />
              </div>
            )}
          </Link>
          <div className="pb-0.5">
            <FollowButton pubkey={pubkey} />
          </div>
        </div>

        {/* ── Identity ── */}
        <div className="mt-2">
          <Link
            to={`/profile/${pubkey}`}
            className="flex items-center gap-1.5 text-sm font-extrabold hover:text-primary"
          >
            <span className="truncate">{name}</span>
            {/* Gated on real directory verification, never on the mere
                presence of a nip05 field. */}
            <VerifiedBadge nip05={profile?.nip05} pubkey={pubkey} size="sm" />
          </Link>
          {profile?.nip05 && (
            <p className="truncate text-[11px] text-primary/70">{profile.nip05}</p>
          )}
        </div>

        {/* ── Bio ── */}
        {about && (
          <div className="mt-2">
            <p
              className={`whitespace-pre-wrap break-words text-[11px] leading-relaxed text-muted-foreground ${
                longBio && !bioOpen ? 'line-clamp-3' : ''
              }`}
            >
              {about}
            </p>
            {longBio && (
              <button
                onClick={() => setBioOpen(o => !o)}
                className="mt-0.5 text-[10px] font-bold text-primary hover:underline"
              >
                {bioOpen ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        )}

        {/* ── Category + social links ── */}
        {(categoryLabel || website) && (
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            {categoryLabel && (
              <span className="rounded-md bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                {categoryLabel}
              </span>
            )}
            {website && (
              <a
                href={website}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
              >
                <Globe className="h-2.5 w-2.5" />
                {hostOf(website)}
              </a>
            )}
          </div>
        )}

        {/* ── Statistics ── */}
        <div className="mt-3 grid grid-cols-4 gap-1.5">
          <Stat icon={Users} value={compact(followerCount)} label="Followers" />
          <Stat icon={Users} value={compact(followingCount)} label="Following" />
          <Stat icon={Radio} value={compact(totalBroadcasts)} label="Streams" />
          <Stat
            icon={Coins}
            value={totalTippedUsd > 0 ? formatUsd(totalTippedUsd) : '$0'}
            label="Tipped"
            accent="text-emerald-500"
          />
        </div>

        {supporterCount > 0 && (
          <div className="mt-2 flex items-center gap-1.5 rounded-xl border border-emerald-500/25 bg-emerald-500/5 px-2.5 py-1.5">
            <Trophy className="h-3 w-3 shrink-0 text-amber-500" />
            <p className="truncate text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
              {supporterCount} supporter{supporterCount === 1 ? '' : 's'} this stream
            </p>
          </div>
        )}

        {/* ── Scheduled ── */}
        <div className="mt-3 space-y-2.5">
          <Collapsible
            icon={Calendar}
            title="Scheduled streams"
            count={scheduled.length}
            defaultOpen
          >
            {scheduled.slice(0, 5).map(s => (
              <BroadcastRow key={s.aTag} stream={s} nip05={profile?.nip05} />
            ))}
          </Collapsible>

          <Collapsible icon={History} title="Previous broadcasts" count={past.length}>
            {past.slice(0, 8).map(s => (
              <BroadcastRow key={s.aTag} stream={s} nip05={profile?.nip05} />
            ))}
          </Collapsible>
        </div>

        {/* ── Support links ── */}
        <div className="mt-3 grid grid-cols-2 gap-1.5">
          <Link
            to={`/support?creator=${pubkey}`}
            className="flex items-center justify-center gap-1.5 rounded-xl bg-emerald-500/10 py-2 text-[11px] font-bold text-emerald-600 transition-colors hover:bg-emerald-500/20 dark:text-emerald-400"
          >
            <Coins className="h-3 w-3" /> Support
          </Link>
          <Link
            to={`/profile/${pubkey}`}
            className="flex items-center justify-center gap-1.5 rounded-xl bg-muted py-2 text-[11px] font-bold text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <Clock className="h-3 w-3" /> Full profile
          </Link>
        </div>
      </div>
    </div>
  );
}
